import { useState } from 'react';
import { Copy, Check, RefreshCw, Globe, Clock, Play } from 'lucide-react';
import { webhookUrl } from '../api.js';
import { TRIGGER_TYPES } from '../constants.js';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button onClick={copy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? 'var(--green)' : 'var(--text-muted)', padding: '4px 6px', borderRadius: 5, flexShrink: 0 }} title="Copy to clipboard">
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

const CRON_PRESETS = [
  { label: 'Every minute',  value: '* * * * *' },
  { label: 'Every 5 min',  value: '*/5 * * * *' },
  { label: 'Every hour',   value: '0 * * * *' },
  { label: 'Daily at midnight', value: '0 0 * * *' },
  { label: 'Weekdays 9am', value: '0 9 * * 1-5' },
  { label: 'Weekly Sunday', value: '0 0 * * 0' },
];

export default function TriggerConfig({ workflow, onChange, onManualTrigger, isTriggerLoading }) {
  const { trigger_type, trigger_config = {}, id } = workflow;

  function setType(type) { onChange({ ...workflow, trigger_type: type, trigger_config: {} }); }
  function setCfg(key, val) { onChange({ ...workflow, trigger_config: { ...trigger_config, [key]: val } }); }

  const ICONS = { webhook: Globe, cron: Clock, manual: Play };

  return (
    <div style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '0' }}>
      {/* Section header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--primary)' }}>
          Trigger
        </span>
      </div>

      <div style={{ padding: '16px 20px', display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Trigger type selector */}
        <div>
          <label className="field-label">Type</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {Object.entries(TRIGGER_TYPES).map(([key, meta]) => {
              const Icon = ICONS[key];
              const active = trigger_type === key;
              return (
                <button
                  key={key}
                  onClick={() => setType(key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px',
                    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                    borderRadius: 8,
                    background: active ? 'var(--primary-glow)' : 'var(--surface-2)',
                    color: active ? 'var(--primary)' : 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: '.8rem',
                    fontWeight: active ? 600 : 400,
                    transition: 'all .15s',
                  }}
                >
                  <Icon size={13} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Webhook config */}
        {trigger_type === 'webhook' && id && (
          <div style={{ flex: 1, minWidth: 280 }}>
            <label className="field-label">Webhook URL (POST here to trigger)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="field-input"
                readOnly
                value={webhookUrl(id)}
                style={{ fontFamily: 'IBM Plex Mono', fontSize: '.78rem', cursor: 'text', flex: 1 }}
              />
              <CopyButton text={webhookUrl(id)} />
            </div>
            <div style={{ marginTop: 10 }}>
              <label className="field-label">Secret (optional)</label>
              <input
                className="field-input"
                value={trigger_config.secret ?? ''}
                onChange={e => setCfg('secret', e.target.value)}
                placeholder="Set to verify X-Webhook-Secret header"
                style={{ fontSize: '.82rem' }}
              />
            </div>
          </div>
        )}

        {trigger_type === 'webhook' && !id && (
          <div style={{ flex: 1 }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginTop: 24 }}>
              Save the workflow to generate a webhook URL.
            </p>
          </div>
        )}

        {/* Cron config */}
        {trigger_type === 'cron' && (
          <div style={{ flex: 1, minWidth: 280 }}>
            <label className="field-label">Cron Expression</label>
            <input
              className="field-input"
              value={trigger_config.cron_expression ?? ''}
              onChange={e => setCfg('cron_expression', e.target.value)}
              placeholder="0 * * * *"
              style={{ fontFamily: 'IBM Plex Mono', fontSize: '.85rem', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CRON_PRESETS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setCfg('cron_expression', p.value)}
                  style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: '.72rem',
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    color: 'var(--text-dim)', cursor: 'pointer',
                  }}
                  className="hover:border-[var(--primary)] hover:text-white transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Manual trigger */}
        {trigger_type === 'manual' && id && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <label className="field-label">Manual trigger</label>
            <button
              className="btn-primary"
              onClick={onManualTrigger}
              disabled={isTriggerLoading}
              style={{ alignSelf: 'flex-start' }}
            >
              {isTriggerLoading
                ? <><RefreshCw size={14} className="spin" /> Running…</>
                : <><Play size={14} /> Run Now</>
              }
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
